import { useTranslation } from "react-i18next";
import { Grid, Button, Skeleton } from "@mui/material";
import MUIDataTable from "mui-datatables";
import NoMatchImage from "../../components/common/NoMatchImage/noMatchImage";
import { NoInfoComponent } from "../../components/common/NoInfoImage/noInfoComponent";
import { useEffect, useState, useRef } from "react";

import { getUser, getUserStatus } from "../userSlice";
import { useSelector } from "react-redux";
import { CODES } from "../../consts/codes";
import {
  avisosFields,
  updateNSSAndNSSUnificadoValue,
} from "../../consts/avisosFields";

import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import AddEmployees from "../../components/common/infonavit/addEmployees/addEmployees";
import { FilterInfonavitRequests } from "../../components/common/infonavit/filterInfonavitRequests";
import CustomTablePagination from "../../components/DataTable/CustomTablePagination";
import { GetDynamicReportConf } from "../../services/user/infonavit/adminDynamicReport";
import {
  GetLocalAvisos,
  GetDirectAvisos,
} from "../../services/user/infonavit/infonavitRequests";
import { GetCredentialsNrps } from "../../services/user/infonavit/adminCredentials";
import {
  stringToDateTimeString,
  stringToDateString,
  formatMxCurrency,
} from "../../utils/functions";

export const InfonavitRequests = () => {
  const userInfo = useSelector(getUser);
  const userStatus = useSelector(getUserStatus);

  const [data, setData] = useState([]);
  const [unfilteredData, setUnfilteredData] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [noContent, setNoContent] = useState(false);

  const [nrpList, setNrpList] = useState([]);
  const [createdEmployees, setCreatedEmployees] = useState(null);

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedAvisos, setSelectedAvisos] = useState([]);

  const [isLoadingRequest, setIsLoadingRequest] = useState(false);
  const updateEmployeesCounter = useRef(0);

  const { t } = useTranslation();

  const [columns, setColumns] = useState([]);

  const options = {
    filterType: "dropdown",
    print: "false",
    filter: false,
    download: false,
    search: false,
    selectToolbarPlacement: "none",
    selectableRows: "multiple",
    selectableRowsHeader: true,
    rowsSelected: selectedRows,
    onRowSelectionChange: (
      currentRowsSelected,
      allRowsSelected,
      rowsSelected
    ) => {
      const selectedEmployees = [];
      const selectedAvisos = [];
      rowsSelected = rowsSelected.filter((index) => {
        const avisoData = data[index];
        const nss = avisoData?.nss;

        if (!selectedEmployees.includes(nss)) {
          selectedEmployees.push(nss);
          selectedAvisos.push(avisoData);

          return true;
        }
        return false;
      });

      setSelectedAvisos(selectedAvisos);
      setSelectedRows(rowsSelected);
    },
    responsive: "standard",
    searchPlaceholder: t("App.infonavitRequests.table.filterPlaceholder"),
    elevation: 0,
    viewColumns: false,
    searchOpen: true,
    rowsPerPageOptions: [10, 15, 20],
    textLabels: {
      body: {
        noMatch: noContent ? (
          <NoMatchImage />
        ) : isLoadingData ? (
          <Skeleton />
        ) : (
          <NoInfoComponent type="dynamic" text="" />
        ),
        toolTip: "Sort",

        columnHeaderTooltip: (column) =>
          t("App.buttonLabels.order", { label: column.label }),
      },
      pagination: {
        next: t("App.userDetail.next"),
        previous: t("App.userDetail.previous"),
        rowsPerPage: t("App.userDetail.resultsPerPage"),
        displayRows: t("App.userDetail.of"),
      },
    },
    customToolbar: ({ displayData }) => {
      return (
        <Grid container gap="2rem" justifyContent="flex-end">
          {!isLoadingRequest ? (
            <></>
          ) : (
            <Button
              type="button"
              startIcon={
                <CloseIcon className="no-search-input" sx={{ fontSize: 22 }} />
              }
              className="btn__outlined__blue"
              onClick={stopEmployeesUpdate}
            >
              {t("App.infonavitRequests.stopBtn")}
            </Button>
          )}

          <Button
            type="button"
            startIcon={
              <SearchOutlinedIcon
                className="no-search-input"
                sx={{ fontSize: 22 }}
              />
            }
            className="btn__filled__blue btn__filled__blue--real"
            disabled={selectedRows.length < 1 || isLoadingRequest}
            onClick={updateSelectedEmployees}
          >
            {t("App.infonavitRequests.btnGroup.singleRequest")}
          </Button>

          <AddEmployees
            nrpList={nrpList}
            setCreatedEmployees={setCreatedEmployees}
          />
        </Grid>
      );
    },
  };

  /**
   * Carga los NRPs
   */
  useEffect(() => {
    if (userStatus !== "succeeded") return;

    fetchConfiguration();

    if (userInfo?.nameRole === "Infocloud Admin") {
      fetchNrps();
    } else {
      setNrpList(
        userInfo.nrps.map((nrp) => {
          return { label: nrp, value: nrp };
        })
      );
    }
  }, [userStatus]);

  /**
   * Consulta los empleados creados manualmente
   */
  useEffect(() => {
    if (userStatus !== "succeeded") return;

    if (createdEmployees) {
      const currentDateString = stringToDateString(new Date()) + " 23:59:59";

      const nrps = [];
      createdEmployees.map((item) => {
        if (!nrps.includes(item.nrp)) nrps.push(item.nrp);
      });

      const payload = {
        nrps,
        initFilterDate: stringToDateTimeString("2005-01-07 00:00:00"),
        endFilterDate: stringToDateTimeString(currentDateString),
      };

      fetchEmployeesAvisos(payload);
    }
  }, [createdEmployees]);

  /**
   * Función que consulta y settea la información de los NRPs
   */
  const fetchNrps = async () => {
    try {
      const mainUser = userInfo.mainUser;
      const {
        status,
        data: { responseStatus, response },
      } = await GetCredentialsNrps(mainUser);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        setNrpList(
          response.map((row, index) => {
            return { label: row.nrp, value: row.nrp };
          })
        );
      } else {
        setNrpList([]);
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  /**
   * Función que consulta y settea las columnas de la tabla
   */
  const fetchConfiguration = async () => {
    try {
      const mainUser = userInfo.mainUser;

      setIsLoadingData(true);

      const {
        status,
        data: { responseStatus, response },
      } = await GetDynamicReportConf(mainUser);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        let rpFields = avisosFields;
        const originalReportFields = response?.reportFields ?? [];

        rpFields =
          originalReportFields.length < 1
            ? rpFields
            : rpFields.filter((field) =>
                originalReportFields.includes(field.name)
              );

        rpFields = rpFields.map((field, index) => {
          return {
            ...field,
            options: {
              filter: false,
              sort: true,
              searchable: true,
            },
          };
        });

        setColumns(rpFields);
      } else {
        setColumns([]);
      }

      setIsLoadingData(false);
    } catch (error) {
      console.log("====================================");
      console.log(error);
      setColumns([]);
      console.log("====================================");
    }
  };

  /**
   * Consulta los empleados (avisos) descargados de Infonavit
   */
  const fetchEmployeesAvisos = async (payload) => {
    try {
      setIsLoadingData(true);

      payload = {
        ...payload,
        mainUser: userInfo.mainUser,
      };

      const {
        status,
        data: { responseStatus, response },
      } = await GetLocalAvisos(payload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        const responseData = response.map((i) => {
          i.facDescuento = formatMxCurrency(i?.facDescuento ?? "0");
          i.monDescuento = formatMxCurrency(i?.monDescuento ?? "0");

          return i;
        });

        // Update NSS and NSSUnificado values ​​if applicable
        const tResponse = responseData.map((row, index) =>
          updateNSSAndNSSUnificadoValue(row)
        );

        let newAvisos = tResponse;
        let newSelectedRows = [];

        // If employees were created before filtering
        if (createdEmployees) {
          // They were created with the value of NSSUnificado in the NSS field,
          // therefore their NSS is updated
          const filteredEmployees = createdEmployees.map((item) =>
            item?.nss.substring(0, 10)
          );
          setCreatedEmployees(null);

          newAvisos = tResponse.filter((aviso) =>
            filteredEmployees.includes(aviso.nss)
          );
        }

        if (selectedAvisos.length > 0) {
          newAvisos = selectedAvisos.concat(newAvisos);
          newSelectedRows = selectedAvisos.map((_, index) => index);

          // Delete duplicates
          newAvisos = newAvisos.filter((item, index, array) => {
            return (
              array.findIndex((auxItem) => {
                // Si es un aviso sin información (no está en la BD)
                if (!item?.pk && !item?.sk)
                  return (
                    item?.nrp === auxItem?.nrp && item?.nss === auxItem?.nss
                  );
                // Si es un aviso con información
                else
                  return item?.pk === auxItem?.pk && item?.sk === auxItem?.sk;
              }) === index
            );
          });
        }

        setData(newAvisos);
        setSelectedRows(newSelectedRows);
        setUnfilteredData(newAvisos);
      } else {
        setData([]);
        setSelectedRows([]);
        setUnfilteredData([]);
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    } finally {
      setIsLoadingData(false);
    }
  };

  /**
   * Limpia los filtros
   */
  const cleanFilters = () => {
    let newAvisos = [];
    let newSelectedRows = [];

    if (selectedAvisos.length > 0) {
      newAvisos = selectedAvisos;
      newSelectedRows = selectedAvisos.map((_, index) => index);
    }

    setData(newAvisos);
    setSelectedRows(newSelectedRows);
    setUnfilteredData(newAvisos);
  };

  /**
   * Recibe el NSS de un empleado, realiza una petición al endpoint de consulta
   * directa, recibe la información de aviso actualizada del empleado y lo actualiza
   * en la tabla de empleados (avisos)
   */
  const fetchDirectEmployeesAvisos = async (nss, nssUnificado) => {
    try {
      const payload = {
        nssQuery: [nssUnificado],
        mainUser: userInfo.mainUser,
      };

      const {
        status,
        data: { responseStatus, response },
      } = await GetDirectAvisos(payload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        const responseData = response.map((i) => {
          i.facDescuento = formatMxCurrency(i?.facDescuento ?? "0");
          i.monDescuento = formatMxCurrency(i?.monDescuento ?? "0");

          return i;
        });

        // Update NSS and NSSUnificado values ​​if applicable
        const employeeAviso = responseData
          .map((row, index) => updateNSSAndNSSUnificadoValue(row))
          .find((aviso) => aviso.nss === nss);
        if (employeeAviso) {
          const currentData = data;
          const employeeAvisoIndex = currentData.findIndex(
            (aviso) => aviso?.nss === nss
          );
          if (employeeAvisoIndex !== -1)
            currentData[employeeAvisoIndex] = employeeAviso;

          setData(currentData);
          setUnfilteredData(currentData);
        }
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  /**
   * Función que actualiza la información de los empleados (avisos) seleccionados
   */
  const updateSelectedEmployees = async () => {
    try {
      setIsLoadingRequest(true);
      const updateCounter = updateEmployeesCounter.current;

      for (const row of selectedRows) {
        // The NSSUnificado field will be used to query Infonavit, and NSS will be
        // used to identify/filter employees
        await fetchDirectEmployeesAvisos(data[row].nss, data[row].nssUnificado);
        if (updateCounter < updateEmployeesCounter.current) return;
      }

      // const promisesArray = selectedRows.map((row) =>
      //   fetchDirectEmployeesAvisos(data[row].nss)
      // );

      // await Promise.allSettled(promisesArray);
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    } finally {
      setIsLoadingRequest(false);
    }
  };

  /**
   * Función que detiene la actualización de los empleados (avisos) seleccionados
   */
  const stopEmployeesUpdate = () => {
    updateEmployeesCounter.current = updateEmployeesCounter.current + 1;
    setIsLoadingRequest(false);
  };

  return (
    <Grid
      container
      gap={2}
      paddingBottom="2rem !important"
      padding={{
        xs: "1.5rem",
        sm: "2.25rem 3rem",
        md: "2.25rem 3rem",
        lg: "4.5rem",
        xl: "4.5rem",
      }}
    >
      <Grid item xs="12">
        <p className="display-large__primary-one">
          {t("App.infonavitRequests.directTitle")}
        </p>
      </Grid>

      <Grid item xs="12">
        <Grid
          container
          gap={2}
          padding="2rem"
          marginBottom="2rem"
          borderRadius="8px"
          style={{ backgroundColor: "white" }}
        >
          <Grid item xs="12">
            <Grid container direction="row" alignItems="center">
              <Grid item>
                <ManageAccountsIcon className="accordion__upload__consultIcon" />
              </Grid>

              <Grid item className="col-flex">
                <p
                  className="display-small__primary-one"
                  style={{ paddingLeft: "1rem" }}
                >
                  {t("App.infonavitRequests.subtitle")}
                </p>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs="12">
            <FilterInfonavitRequests
              handleFilters={fetchEmployeesAvisos}
              cleanFilters={cleanFilters}
              setNoContent={setNoContent}
              setFilterPayload={() => {}}
              unfilteredData={unfilteredData}
              nrpList={nrpList}
              data={data}
            />
          </Grid>

          <Grid item xs="12">
            <MUIDataTable
              className="dataTable__base dataTable__base--custom-toolbar"
              style={{ marginTop: "5%", display: "none" }}
              components={{
                TableFooter: (props) => (
                  <CustomTablePagination paginationProps={props} />
                ),
              }}
              data={data}
              columns={columns}
              options={options}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
